/**
 * One bar per day, stacked by outcome: succeeded at the base, then aborted,
 * then canceled. Height is the day's run count against the busiest day in the
 * strip. Each bar is a real button — click or arrow keys select that day — and
 * today's bar is hatched because its numbers are still coming in.
 */
import { useRef } from "react";
import PropTypes from "prop-types";
import { describeDay, maxRuns, showDayLabel } from "./robotTelemetry";

export const OUTCOME_COLORS = {
  succeeded: "var(--bq-success)",
  aborted: "var(--bq-danger)",
  canceled: "var(--bq-text2)",
};

const SEGMENTS = ["succeeded", "aborted", "canceled"];
const PLOT_HEIGHT = 76;

const Swatch = ({ color, hatched, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span
      aria-hidden="true"
      style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        background: hatched
          ? "repeating-linear-gradient(135deg, var(--bq-text3) 0 2px, transparent 2px 4px)"
          : color,
        border: hatched ? "1px solid var(--bq-text3)" : "none",
      }}
    />
    {label}
  </span>
);

Swatch.propTypes = { color: PropTypes.string, hatched: PropTypes.bool, label: PropTypes.string };

export const DayLegend = () => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
    <Swatch color={OUTCOME_COLORS.succeeded} label="Succeeded" />
    <Swatch color={OUTCOME_COLORS.aborted} label="Aborted" />
    <Swatch color={OUTCOME_COLORS.canceled} label="Canceled" />
    <Swatch hatched label="Today so far" />
  </div>
);

const KEY_STEPS = { ArrowLeft: -1, ArrowRight: 1 };

const DayBars = ({ days, selected, today, onSelect }) => {
  const buttons = useRef([]);
  const top = maxRuns(days);
  const count = days.length;

  const focusAt = (index) => {
    const clamped = Math.max(0, Math.min(count - 1, index));
    onSelect(days[clamped].date);
    buttons.current[clamped]?.focus();
  };

  const onKeyDown = (event) => {
    const current = days.findIndex((d) => d.date === selected);
    if (event.key in KEY_STEPS) {
      event.preventDefault();
      focusAt(current + KEY_STEPS[event.key]);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAt(count - 1);
    }
  };

  const columns = `repeat(${count}, minmax(0, 1fr))`;

  return (
    <div>
      {/* A fieldset, not a role="group" div: the native grouping element names
          the set of day buttons for every assistive technology. */}
      <fieldset
        style={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: count > 14 ? 2 : 6,
          alignItems: "end",
          height: PLOT_HEIGHT,
          margin: 0,
          padding: 0,
          minInlineSize: 0,
          border: "none",
          borderBottom: "1px solid var(--bq-border)",
        }}
      >
        <legend className="rt-sr">Runs per day. Use the arrow keys to move between days.</legend>
        {days.map((day, i) => {
          const isSelected = day.date === selected;
          const partial = day.date === today && day.runs > 0;
          const heightPct = (day.runs / top) * 100;
          return (
            <button
              key={day.date}
              ref={(el) => {
                buttons.current[i] = el;
              }}
              type="button"
              className={`rt-bar${partial ? " rt-bar--partial" : ""}`}
              aria-pressed={isSelected}
              aria-label={describeDay(day, today)}
              title={describeDay(day, today)}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => onSelect(day.date)}
              onKeyDown={onKeyDown}
            >
              {day.runs > 0 ? (
                <span className="rt-stack" style={{ height: `${heightPct}%` }}>
                  {SEGMENTS.filter((key) => day[key] > 0).map((key) => (
                    <span
                      key={key}
                      style={{ flex: `${day[key]} 0 0`, background: OUTCOME_COLORS[key] }}
                    />
                  ))}
                </span>
              ) : (
                <span
                  style={{ height: 2, borderRadius: 1, background: "var(--bq-borderStrong)" }}
                />
              )}
            </button>
          );
        })}
      </fieldset>

      <div
        aria-hidden="true"
        style={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: count > 14 ? 2 : 6,
          marginTop: 5,
          fontSize: 10.5,
          color: "var(--bq-text3)",
          textAlign: "center",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {days.map((day, i) => {
          const isSelected = day.date === selected;
          return (
            <span
              key={day.date}
              style={{
                fontWeight: isSelected ? 700 : 400,
                color: isSelected ? "var(--bq-accent)" : undefined,
                overflow: "hidden",
              }}
            >
              {showDayLabel(i, count, day.date, selected, today) ? Number(day.date.slice(8)) : ""}
            </span>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "4px 16px",
          marginTop: 8,
          fontSize: 11.5,
          color: "var(--bq-text3)",
        }}
      >
        <DayLegend />
        <span>
          Tallest bar {top} {top === 1 ? "run" : "runs"}
        </span>
      </div>
    </div>
  );
};

DayBars.propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      runs: PropTypes.number,
      succeeded: PropTypes.number,
      aborted: PropTypes.number,
      canceled: PropTypes.number,
    })
  ).isRequired,
  selected: PropTypes.string.isRequired,
  today: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default DayBars;
